import React, { useEffect, useState } from "react";
import { FaPills, FaPrescription } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import InventoryDashboard from "../Inventory/InventoryDashboard";
import PrescriptionDashboard from "../Prescription/PrescriptionDashboard";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { useAtom } from "jotai";
import { clinicAtom, pharmacyAtom } from "../../_helper/constant/constant-atom";
import { useCurrentRole } from "../../_helper/constant/Permission";

const PharmacyOperationWrapper = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get("type") || "Prescriptions";
  const [activeSection, setActiveSection] = useState(sectionParam);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // This state is used only when a clinic exists
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);

  const [clinic] = useAtom(clinicAtom);
  const [pharmacy] = useAtom(pharmacyAtom);

  const navigationItems = [
    { key: "Prescriptions", label: "Prescriptions", icon: <FaPrescription /> },
    { key: "Inventory", label: "Inventory", icon: <FaPills /> },
  ];

  // When clinic exists, build a query to fetch the pharmacies related to that clinic.
  const clinicQuery = clinic?.id
    ? `?action=getPharmacy&clinic_id=${clinic.id}`
    : null;
  const { data: pharmacies, isFetched } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    clinicQuery,
    !!clinic?.id
  );

  // When a clinic is present and pharmacies are fetched, default to the first pharmacy.
  useEffect(() => {
    if (clinic?.id && isFetched && pharmacies?.length > 0) {
      setSelectedPharmacy(pharmacies[0]?.id);
    }
  }, [clinic?.id, isFetched, pharmacies]);

  useEffect(() => {
    setActiveSection(sectionParam);
  }, [sectionParam]);

  const handleNavigation = (section) => {
    setSearchParams({ type: section });
    setActiveSection(section);
  };

  // Determine the current pharmacy id to pass to dashboards.
  // If a clinic exists, we use the selected pharmacy from the dropdown.
  // Otherwise, we use the pharmacy from the pharmacyAtom.
  const currentPharmacyId = clinic?.id ? selectedPharmacy : pharmacy?.id;

  const renderActiveSection = () => {
    switch (activeSection) {
      case "Inventory":
        return <InventoryDashboard pharmacy={currentPharmacyId} />;
      case "Prescriptions":
        return <PrescriptionDashboard pharmacy={currentPharmacyId} />;
      default:
        return <div>Select a section from the navigation.</div>;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <header className="bg-white shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          {/* App Title */}
          <div className="flex items-center space-x-2">
            <FaPrescription className="text-[#4CAF50] text-2xl" />
            <h1 className="text-lg font-bold text-gray-800">Pharmacy Ops</h1>
          </div>

          {/* Pharmacy Selector or Display */}
          <div className="relative w-50">
            {clinic?.id ? (
              // When a clinic exists, show a dropdown selector.
              <select
                value={selectedPharmacy}
                onChange={(e) => setSelectedPharmacy(e.target.value)}
                className="block w-full bg-white text-lg font-semibold text-gray-800 px-4 py-3 appearance-none cursor-pointer transition-all duration-200 hover:shadow-lg"
              >
                {pharmacies?.map((pharmacy, index) => (
                  <option
                    key={index}
                    value={pharmacy.id}
                    className="text-base font-medium text-gray-700"
                  >
                    {pharmacy.pharmacy_name}
                  </option>
                ))}
              </select>
            ) : (
              // When no clinic exists, display the pharmacy name from pharmacyAtom.
              <div className="block w-full bg-white text-lg font-semibold text-gray-800 px-4 py-3">
                {pharmacy?.pharmacy_name}
              </div>
            )}

            {/* Custom Dropdown Icon (only shown if the dropdown is rendered) */}
            {clinic?.id && (
              <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none -m-4">
                <svg
                  className="w-6 h-6 text-gray-500 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-2">
            {navigationItems.map((item) => (
              <button
                key={item.key}
                onClick={() => handleNavigation(item.key)}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeSection === item.key
                    ? "bg-[#4CAF50] text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-gray-600 hover:text-[#4CAF50] text-2xl"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            ☰
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden bg-white border-t">
            {navigationItems.map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  handleNavigation(item.key);
                  setIsMobileMenuOpen(false);
                }}
                className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                  activeSection === item.key
                    ? "bg-[#4CAF50] text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              </button>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content Area */}
      <main className="flex-grow bg-gray-100 p-4 overflow-auto">
        <div className="max-w-full">{renderActiveSection()}</div>
      </main>
    </div>
  );
};

export default PharmacyOperationWrapper;
